import React from 'react';
import classNames from 'classnames';
import { AppButton } from '@pro-sto/ui';
import { DotsThreeVerticalIcon, PencilIcon, PlusIcon } from '@/components/icons';
import type { ShiftStatus } from '@/types/shift.types';
import type { ShiftsView } from './shiftWeekModel';
import './ShiftsWeek.css';

const VIEW_TABS: { id: ShiftsView; label: string }[] = [
  { id: 'day', label: 'День' },
  { id: 'week', label: 'Неделя' },
];

const ChevronLeftIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M12.5 15L7.5 10L12.5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRightIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M7.5 15L12.5 10L7.5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface ShiftsToolbarProps {
  view: ShiftsView;
  weekTitle: string;
  dayStatus?: ShiftStatus;
  /** День ещё не в загруженном интервале: не показывать «Создать смену». */
  hideCreate?: boolean;
  shiftBusy?: boolean;
  shiftOpening?: boolean;
  shiftClosing?: boolean;
  onViewChange: (view: ShiftsView) => void;
  onShiftWeek: (direction: -1 | 1) => void;
  onCreateShift: () => void;
  onEditShift: () => void;
  onOpenShift: () => void;
  onCloseShift: () => void;
}

const CloseShiftIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ShiftsToolbar: React.FC<ShiftsToolbarProps> = ({
  view,
  weekTitle,
  dayStatus,
  hideCreate = false,
  shiftBusy = false,
  shiftOpening = false,
  shiftClosing = false,
  onViewChange,
  onShiftWeek,
  onCreateShift,
  onEditShift,
  onOpenShift,
  onCloseShift,
}) => (
  <>
    <div className="employees-page__subtabs" role="tablist" aria-label="Вид смен">
      {VIEW_TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={view === tab.id}
          className={classNames('employees-page__subtab', {
            'employees-page__subtab_active': view === tab.id,
          })}
          onClick={() => onViewChange(tab.id)}
          data-testid={`shifts-view-${tab.id}`}
        >
          {tab.label}
        </button>
      ))}
    </div>

    <div className="shifts-toolbar__aside">
      <div className="shifts-toolbar__nav">
        <button
          type="button"
          className="shifts-toolbar__nav-button"
          onClick={() => onShiftWeek(-1)}
          aria-label={view === 'day' ? 'Предыдущий день' : 'Предыдущая неделя'}
          data-testid="shifts-prev-week"
        >
          <ChevronLeftIcon />
        </button>
        <span className="shifts-toolbar__title" data-testid="shifts-week-title">
          {weekTitle}
        </span>
        <button
          type="button"
          className="shifts-toolbar__nav-button"
          onClick={() => onShiftWeek(1)}
          aria-label={view === 'day' ? 'Следующий день' : 'Следующая неделя'}
          data-testid="shifts-next-week"
        >
          <ChevronRightIcon />
        </button>
      </div>

      {!dayStatus && !hideCreate ? (
        <AppButton
          variant="primary"
          iconRight={<PlusIcon size={20} />}
          onClick={onCreateShift}
          data-testid="shifts-create-button"
        >
          Создать смену
        </AppButton>
      ) : null}

      {dayStatus === 'created' || dayStatus === 'open' ? (
        <AppButton
          variant={dayStatus === 'open' ? 'primary' : 'secondary'}
          iconRight={<PencilIcon size={20} />}
          disabled={shiftBusy}
          onClick={onEditShift}
          data-testid="shifts-day-edit"
        >
          Редактировать
        </AppButton>
      ) : null}

      {dayStatus === 'created' ? (
        <AppButton
          variant="primary"
          loading={shiftOpening}
          disabled={shiftBusy && !shiftOpening}
          onClick={onOpenShift}
          data-testid="shifts-day-open"
        >
          Открыть смену
        </AppButton>
      ) : null}

      {dayStatus === 'open' ? (
        <AppButton
          variant="secondary"
          iconRight={<CloseShiftIcon />}
          loading={shiftClosing}
          disabled={shiftBusy && !shiftClosing}
          onClick={onCloseShift}
          data-testid="shifts-day-close"
        >
          Закрыть
        </AppButton>
      ) : null}

      {((!dayStatus && !hideCreate) || dayStatus === 'open') ? (
        <button
          type="button"
          className="shifts-toolbar__menu"
          disabled
          aria-label="Меню смены"
          data-testid="shifts-menu-button"
        >
          <DotsThreeVerticalIcon size={20} />
        </button>
      ) : null}
    </div>
  </>
);

ShiftsToolbar.displayName = 'ShiftsToolbar';

export default ShiftsToolbar;
